import { useCallback, useState } from 'react';
import { ApiError } from '@/lib/api-client';
import { regenScene, type RegenSceneResponse } from './api';

/**
 * Position-1 Commit-4 (Sebastian ruling 2026-09-04) — customer
 * re-render mutation hook. Mirrors `useSceneTextEdit` +
 * `usePanelMove` in shape.
 *
 * Fires `POST /api/book/orders/:orderId/scenes/:sceneId/regen` when
 * the customer confirms option 3 of the Freigabe change dialog.
 * Comment string (possibly empty) is trimmed on the client and sent
 * to the server, which injects it verbatim into the image prompt at
 * render time. No moderation on this surface (removed 2026-09-04
 * because the audio product's story-topic classifier rejects the most
 * common shape of an image correction — removals like "keine Tram").
 * Content-safety belongs to the Jugendschutz workstream.
 *
 * `already_saved`-style shortcut does not apply here (the server
 * has no idempotency key for regens; every accepted request enqueues
 * a fresh render).
 */

export type RegenSceneResult =
  | { readonly ok: true; readonly response: RegenSceneResponse }
  | { readonly ok: false; readonly error: ApiError | null };

export interface UseSceneRegenState {
  readonly saving: boolean;
  readonly error: ApiError | null;
  readonly save: (sceneId: string, comment: string | null) => Promise<RegenSceneResult>;
}

export function useSceneRegen(orderId: string): UseSceneRegenState {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const save = useCallback(
    async (sceneId: string, comment: string | null): Promise<RegenSceneResult> => {
      setSaving(true);
      setError(null);
      try {
        const response = await regenScene(orderId, sceneId, comment);
        return { ok: true, response };
      } catch (err) {
        const apiError = err instanceof ApiError ? err : null;
        setError(apiError);
        return { ok: false, error: apiError };
      } finally {
        setSaving(false);
      }
    },
    [orderId],
  );

  return { saving, error, save };
}
